// src/pages/Privacy.js
import React from 'react';
import { Box, Container, CssBaseline, Divider, Link, Paper, Stack, Typography } from '@mui/material';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { SITE, emailHref, phoneHref } from '../siteInfo';
import { useSeo } from '../seo';

/**
 * The notice POPIA (section 18) asks for: what is collected, why, whether it
 * has to be given, who else sees it, and what the customer can do about it.
 *
 * Every statement here describes what the code actually does — the fields the
 * checkout asks for, the marketing box it offers, what functions/index.js
 * passes to Yoco and PayFast, and who firestore.rules lets read an order.
 * Change one of those and this page has to change with it.
 */
const UPDATED = '19 October 2026';

const Section = ({ title, children }) => (
  <Box component="section">
    <Typography component="h2" sx={{ fontWeight: 700, fontSize: '1.1rem', mb: 1.25 }}>
      {title}
    </Typography>
    <Stack spacing={1.25} sx={{ color: 'text.secondary', lineHeight: 1.75 }}>
      {children}
    </Stack>
  </Box>
);

const Item = ({ label, children }) => (
  <Typography component="li" sx={{ lineHeight: 1.75 }}>
    <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
      {label}
    </Box>{' '}
    {children}
  </Typography>
);

const List = ({ children }) => (
  <Box component="ul" sx={{ m: 0, pl: 2.5, '& > li + li': { mt: 0.75 } }}>
    {children}
  </Box>
);

const Privacy = () => {
  useSeo({
    title: 'Privacy notice | Crestancia',
    description: 'How Crestancia collects, uses and protects your personal information under POPIA.',
    path: '/privacy',
  });

  const contact = (
    <Link href={emailHref} underline="hover">
      {SITE.email}
    </Link>
  );

  return (
    <>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Header />
        <Container maxWidth="md" sx={{ flexGrow: 1, py: { xs: 4, md: 6 } }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
            Privacy notice
          </Typography>
          <Typography sx={{ mt: 1, color: 'text.secondary' }}>
            How {SITE.name} handles your personal information, in line with the Protection of
            Personal Information Act (POPIA). Last updated {UPDATED}.
          </Typography>

          <Paper
            elevation={0}
            sx={{
              mt: 4,
              p: { xs: 2.5, md: 4 },
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.paper',
            }}
          >
            <Stack spacing={3.5} divider={<Divider flexItem />}>
              <Section title="Who we are">
                <Typography>
                  {SITE.name}, {SITE.address}. Questions or requests about your personal
                  information go to {contact} or{' '}
                  <Link href={phoneHref} underline="hover">
                    {SITE.phone}
                  </Link>
                  .
                </Typography>
              </Section>

              <Section title="What we collect and why">
                <List>
                  <Item label="Your account:">
                    your email address, and your name if you sign in with Google. We use it to sign
                    you in and to show you your orders.
                  </Item>
                  <Item label="When you order:">
                    your name, email address, phone number and delivery address. We use them to
                    process and deliver your order and to send you its confirmation.
                  </Item>
                  <Item label="Your phone number:">
                    to contact you about your order and its delivery, and for marketing only if you
                    agree to it.
                  </Item>
                  <Item label="Marketing:">
                    only if you tick the box at checkout. We then use your name, email address and
                    phone number to send you news and specials about our own products by email,
                    SMS or WhatsApp, and we keep a record of when you agreed.
                  </Item>
                  <Item label="Tax invoices:">
                    if you ask for one, the company name, contact name, VAT and registration
                    numbers and billing address you give us, so we can issue it.
                  </Item>
                  <Item label="Payments:">
                    card and bank details are entered on Yoco's or PayFast's own page. We never
                    see or store them. If you pay us directly, we keep the proof of payment you
                    send.
                  </Item>
                </List>
                <Typography>
                  Your name, email address, phone number and delivery address are needed to place
                  an order online. Without them we cannot deliver it. Tax invoice details are
                  optional, and so is marketing: saying no makes no difference to your order.
                </Typography>
              </Section>

              <Section title="Who else sees it">
                <List>
                  <Item label="The payment provider you choose">
                    receives your email address, and PayFast your name as well, so it can take the
                    payment. Your phone number and address are not sent to either provider.
                  </Item>
                  <Item label="Google Firebase">
                    hosts this site and stores orders on our behalf. Its servers may be outside
                    South Africa.
                  </Item>
                  <Item label="Our email provider">
                    sends our order confirmations, invoices and, if you agreed, our marketing on
                    our behalf.
                  </Item>
                  <Item label="Our staff and drivers">
                    see what they need to prepare and deliver your order.
                  </Item>
                </List>
                <Typography>
                  We do not sell your information, or give it to anyone else for their own
                  marketing.
                </Typography>
              </Section>

              <Section title="How we look after it">
                <Typography>
                  Only you, when signed in, and our administrators can see your orders. We keep
                  order and invoice records for as long as South African tax law requires us to
                  keep sales records, and no longer than we need them.
                </Typography>
              </Section>

              <Section title="Your rights">
                <Typography>
                  You can stop our marketing at any time, free of charge: use the unsubscribe link
                  in any marketing email, or tell us by email or phone, and we will stop.
                </Typography>
                <Typography>
                  You may also ask to see the personal information we hold about you, to have it
                  corrected, or to have it deleted where we are not required by law to keep it,
                  and you may object to how we use it. Email {contact} and we will respond.
                </Typography>
                <Typography>
                  If you are not satisfied with our response, you may complain to the Information
                  Regulator at{' '}
                  <Link href="https://inforegulator.org.za" target="_blank" rel="noreferrer" underline="hover">
                    inforegulator.org.za
                  </Link>
                  .
                </Typography>
              </Section>
            </Stack>
          </Paper>
        </Container>
        <Footer />
      </Box>
    </>
  );
};

export default Privacy;
